import { Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { getApiErrorMessage, getDepartment, listTeams } from '../../api/client';
import type { DepartmentDetail, TeamListItem } from '../../api/types';
import Badge from '../../components/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { canWriteDepartments } from '../../utils/permissions';
import { PLATFORM_CONFIG } from '../platform-config/paths';

export default function TeamsPage() {
  const { deptId } = useParams<{ deptId: string }>();
  const { user } = useAuth();
  const [department, setDepartment] = useState<DepartmentDetail | null>(null);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const canEdit = user && canWriteDepartments(user);

  useEffect(() => {
    if (!deptId) return;
    setLoading(true);
    Promise.all([getDepartment(deptId), listTeams(deptId)])
      .then(([dept, teamRows]) => {
        setDepartment(dept);
        setTeams(teamRows);
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [deptId]);

  if (!deptId) {
    return <div className="alert alert-error">Department not specified.</div>;
  }

  return (
    <>
      <div className="page-header" style={{ marginTop: 0 }}>
        <div>
          <Link to={PLATFORM_CONFIG.department(deptId)} style={{ fontSize: '0.85rem' }}>
            ← {department?.name ?? 'Department'}
          </Link>
          <h1 style={{ marginTop: '0.35rem' }}>Teams</h1>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Support levels and round-robin assignment pools within this department.
          </p>
        </div>
        {canEdit && (
          <Link to={PLATFORM_CONFIG.departmentTeamsNew(deptId)} className="btn btn-primary">
            Add team
          </Link>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        {loading ? (
          <div className="loading">Loading teams…</div>
        ) : teams.length === 0 ? (
          <div className="table-empty-state">
            <Users size={28} strokeWidth={1.5} aria-hidden />
            <p>No teams yet</p>
            {canEdit && (
              <Link to={PLATFORM_CONFIG.departmentTeamsNew(deptId)} className="btn btn-primary btn-sm">
                Add team
              </Link>
            )}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Level</th>
                  <th>First line</th>
                  <th>Members</th>
                  <th>Channels</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {teams.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <Link to={PLATFORM_CONFIG.departmentTeam(deptId, t.id)}>{t.name}</Link>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        {t.displayLevel}
                      </div>
                    </td>
                    <td>L{t.level}</td>
                    <td>{t.isFirstLine ? <Badge label="First line" className="badge-info" /> : '—'}</td>
                    <td>{t._count.members}</td>
                    <td>{t._count.channelMappings || 'All'}</td>
                    <td>
                      <Badge
                        label={t.isActive ? 'Active' : 'Inactive'}
                        className={t.isActive ? 'badge-success' : 'badge-neutral'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
